/* v27.16 - Pink Stork cart drawer recommendation engine.
   See snippets/cart-recommendations.liquid for the ranking rules.
   v31.0 - also handles the Beef Organ Complex bundle UI
   (snippets/ps-bo-row.liquid + snippets/ps-bo-card.liquid): scent toggle
   (swaps the card image too) + one-tap add. */
(function () {
  'use strict';

  var SECTIONS = 'cart-items,cart-footer,cart-item-count,cart-live-region';
  var recCache = {};      // product_id -> array of { product, source }
  var busy = false;
  var queued = false;

  function money(cents) {
    var cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function imgUrl(src, width) {
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }

  // Normalize product JSON from /recommendations/products.json
  function fromAjax(p) {
    var v = (p.variants || []).filter(function (x) { return x.available; })[0] || (p.variants || [])[0] || {};
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      url: p.url || '/products/' + p.handle,
      image: imgUrl(p.featured_image || (p.images && p.images[0]), 200),
      variant: v.id,
      price: typeof v.price === 'number' ? v.price : Math.round(parseFloat(v.price || 0) * 100),
      available: !!(p.available && v.available),
      gift: !!p.gift_card || /gift.?card/i.test((p.type || '') + ' ' + p.handle)
    };
  }

  function fetchRecs(productId, intent) {
    var key = productId + ':' + intent;
    if (recCache[key]) return Promise.resolve(recCache[key]);
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return fetch(root + 'recommendations/products.json?product_id=' + productId + '&limit=10&intent=' + intent)
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (d) { recCache[key] = (d.products || []).map(fromAjax); return recCache[key]; })
      .catch(function () { return []; });
  }

  function readPool(root) {
    var el = root.querySelector('[data-ps-cart-recs-pool]');
    try { return JSON.parse(el.textContent); } catch (e) { return { pinned: [], fallback: [], exclude: [] }; }
  }

  function render(root) {
    if (busy) { queued = true; return; }
    busy = true;
    var pool = readPool(root);
    var threshold = parseInt(root.dataset.threshold, 10) || 0;
    var count = parseInt(root.dataset.count, 10) || 4;

    fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (!cart.items.length) { root.hidden = true; return; }
        var inCart = {};
        cart.items.forEach(function (i) { inCart[i.product_id] = true; });
        var safe = {}, sensitiveCart = false;
        (pool.safe || []).forEach(function (id) { safe[id] = true; });
        (pool.sensitive || []).forEach(function (id) { if (inCart[id]) sensitiveCart = true; });
        var excluded = {};
        (pool.exclude || []).forEach(function (id) { excluded[id] = true; });

        // Up to 3 distinct cart products drive the recommendations.
        var drivers = [];
        cart.items.forEach(function (i) { if (drivers.indexOf(i.product_id) === -1) drivers.push(i.product_id); });
        drivers = drivers.slice(0, 3);

        var jobs = [];
        drivers.forEach(function (id, idx) {
          jobs.push(fetchRecs(id, 'complementary').then(function (list) { return { list: list, weight: 4 - idx * 0.5 }; }));
          jobs.push(fetchRecs(id, 'related').then(function (list) { return { list: list, weight: 3 - idx * 0.5 }; }));
        });

        return Promise.all(jobs).then(function (results) {
          var scored = {};
          function add(p, weight, rankBonus) {
            if (!p || !p.id || !p.available || !p.variant || p.gift || inCart[p.id] || excluded[p.id] || !(p.price > 0)) return;
            // Pregnant / nursing / TTC carts only ever see pregnancy-friendly products.
            if (sensitiveCart && !safe[p.id]) return;
            if (!scored[p.id]) scored[p.id] = { p: p, score: 0 };
            scored[p.id].score += weight + (rankBonus || 0);
          }
          (pool.pinned || []).forEach(function (p) { add(p, 5); });
          results.forEach(function (r) {
            r.list.forEach(function (p, i) { add(p, r.weight, (10 - i) * 0.05); });
          });
          (pool.fallback || []).forEach(function (p, i) { add(p, 1, (24 - i) * 0.01); });

          var list = Object.keys(scored).map(function (k) { return scored[k]; });
          var remaining = threshold - cart.items_subtotal_price;
          var picks;
          if (threshold > 0 && remaining > 0) {
            var closes = list.filter(function (x) { return x.p.price >= remaining; })
              .sort(function (a, b) { return (a.p.price - b.p.price) || (b.score - a.score); });
            // Among products that close the gap, prefer strong matches: keep the
            // best-matching 2 near the front even if slightly pricier.
            var bestMatch = closes.slice().sort(function (a, b) { return b.score - a.score; }).slice(0, 2);
            var ordered = [];
            closes.slice(0, 1).concat(bestMatch, closes).forEach(function (x) { if (ordered.indexOf(x) === -1) ordered.push(x); });
            var rest = list.filter(function (x) { return ordered.indexOf(x) === -1; })
              .sort(function (a, b) { return b.score - a.score; });
            picks = ordered.concat(rest).slice(0, count);
            picks.forEach(function (x) { x.unlocks = x.p.price >= remaining; });
          } else {
            picks = list.sort(function (a, b) { return b.score - a.score; }).slice(0, count);
            picks.forEach(function (x) { x.unlocks = false; });
          }

          if (!picks.length) { root.hidden = true; return; }

          var heading = root.querySelector('[data-ps-recs-heading]');
          if (threshold > 0 && remaining > 0) {
            heading.innerHTML = esc(root.dataset.headingUnder).replace('[amount]', '<strong>' + esc(money(remaining)) + '</strong>');
          } else {
            heading.textContent = root.dataset.headingOver;
          }

          var track = root.querySelector('[data-ps-recs-track]');
          track.innerHTML = picks.map(function (x) {
            var p = x.p;
            return '<li class="ps-cart-recs__card">' +
              '<a class="ps-cart-recs__img" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true">' +
                (p.image ? '<img src="' + esc(p.image) + '" alt="" loading="lazy" width="72" height="72">' : '') +
              '</a>' +
              '<div class="ps-cart-recs__info">' +
                (x.unlocks ? '<span class="ps-cart-recs__tag">Unlocks free shipping</span>' : '') +
                '<a class="ps-cart-recs__title" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
                '<div class="ps-cart-recs__row">' +
                  '<span class="ps-cart-recs__price">' + esc(money(p.price)) + '</span>' +
                  '<button type="button" class="ps-cart-recs__add" data-ps-recs-add="' + p.variant + '" aria-label="Add ' + esc(p.title) + ' to cart">+ Add</button>' +
                '</div>' +
              '</div>' +
            '</li>';
          }).join('');
          track.scrollLeft = 0;
          root.hidden = false;
          updateArrows(root);
        });
      })
      .catch(function () { root.hidden = true; })
      .then(function () {
        busy = false;
        if (queued) { queued = false; render(root); }
      });
  }

  function updateArrows(root) {
    var track = root.querySelector('[data-ps-recs-track]');
    var prev = root.querySelector('[data-ps-recs-prev]');
    var next = root.querySelector('[data-ps-recs-next]');
    if (!track || !prev || !next) return;
    var max = track.scrollWidth - track.clientWidth - 2;
    prev.disabled = track.scrollLeft <= 2;
    next.disabled = track.scrollLeft >= max;
    root.classList.toggle('ps-cart-recs--static', max <= 0);
  }

  function all() { return document.querySelectorAll('[data-ps-cart-recs]'); }
  function refreshAll() { all().forEach(render); }

  // Add to cart, then let the theme repaint the drawer exactly like its own
  // quantity buttons do (shapes:cart:update with fresh section HTML).
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ps-recs-add]');
    if (btn) {
      if (btn.disabled) return;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding';
      fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(btn.dataset.psRecsAdd), quantity: 1 }], sections: SECTIONS })
      })
        .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
        .then(function (res) {
          btn.textContent = 'Added';
          var status = btn.closest('[data-ps-cart-recs]').querySelector('[data-ps-recs-status]');
          if (status) status.textContent = 'Added to cart.';
          window.dispatchEvent(new CustomEvent('shapes:cart:update', { detail: { response: res } }));
        })
        .catch(function () {
          btn.textContent = label;
          btn.disabled = false;
        });
      return;
    }
    var arrow = e.target.closest('[data-ps-recs-prev],[data-ps-recs-next]');
    if (arrow) {
      var root = arrow.closest('[data-ps-cart-recs]');
      var track = root.querySelector('[data-ps-recs-track]');
      var card = track.querySelector('.ps-cart-recs__card');
      var step = card ? card.getBoundingClientRect().width + 12 : 220;
      track.scrollBy({ left: arrow.hasAttribute('data-ps-recs-prev') ? -step : step, behavior: 'smooth' });
      setTimeout(function () { updateArrows(root); }, 400);
    }
  }, true); // capture phase: the drawer panel uses @click.stop, which would swallow a normal listener

  // v31.0 - Beef Organ Complex bundle UI (snippets/ps-bo-row.liquid +
  // snippets/ps-bo-card.liquid). Scent toggle swaps which variant ADD adds
  // (and the card image); ADD / the "Add 1 more" nudge add one unit
  // (one-time) and repaint the drawer. Capture phase for the same
  // @click.stop reason as above.
  document.addEventListener('click', function (e) {
    var opt = e.target.closest('[data-ps-bo-variant]');
    if (opt) {
      e.preventDefault();
      var box = opt.closest('.ps-bo--claim');
      if (!box) return;
      var vid = opt.getAttribute('data-ps-bo-variant');
      box.querySelectorAll('[data-ps-bo-variant]').forEach(function (b) {
        var on = b === opt;
        b.classList.toggle('is-on', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      box.querySelectorAll('[data-ps-bo-img]').forEach(function (im) {
        im.hidden = im.getAttribute('data-ps-bo-img') !== vid;
      });
      var addBtn = box.querySelector('[data-ps-bo-add]');
      if (addBtn) addBtn.setAttribute('data-ps-bo-add', vid);
      return;
    }
    var btn = e.target.closest('[data-ps-bo-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.disabled) return;
    btn.disabled = true;
    btn.classList.add('is-busy');
    fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.getAttribute('data-ps-bo-add')), quantity: 1 }], sections: SECTIONS })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (res) {
        window.dispatchEvent(new CustomEvent('shapes:cart:update', { detail: { response: res } }));
      })
      .catch(function () { btn.disabled = false; btn.classList.remove('is-busy'); });
  }, true);

  // v27.18 - one-tap "Add to bag" on product cards (homepage Best sellers,
  // collection pages). Adds one unit, repaints the side cart and opens it.
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ps-quick-add]');
    if (!btn || btn.disabled) return;
    e.preventDefault();
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding';
    fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(btn.dataset.psQuickAdd), quantity: 1 }], sections: SECTIONS })
    })
      .then(function (r) { if (!r.ok) throw new Error('add failed'); return r.json(); })
      .then(function (res) {
        window.dispatchEvent(new CustomEvent('shapes:cart:update', { detail: { response: res } }));
        try { window.Alpine && Alpine.store('modals').open('cart'); } catch (err) {}
        btn.textContent = 'Added';
        setTimeout(function () { btn.textContent = label; btn.disabled = false; }, 1800);
      })
      .catch(function () { btn.textContent = label; btn.disabled = false; });
  }, true);

  document.addEventListener('scroll', function (e) {
    if (e.target && e.target.matches && e.target.matches('[data-ps-recs-track]')) {
      updateArrows(e.target.closest('[data-ps-cart-recs]'));
    }
  }, true);

  ['shapes:cart:update', 'shapes:cart:afteradditem', 'shapes:cart:cartqtychange'].forEach(function (ev) {
    window.addEventListener(ev, function () { setTimeout(refreshAll, 50); });
  });

  // The drawer markup is teleported in lazily, so watch for it to appear.
  var seen = new WeakSet();
  function scan() {
    all().forEach(function (root) {
      if (!seen.has(root)) { seen.add(root); render(root); }
    });
  }
  var pending = false;
  new MutationObserver(function () {
    if (pending) return;
    pending = true;
    requestAnimationFrame(function () { pending = false; scan(); });
  }).observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', scan); else scan();
})();
